import React from 'react'
import { getApiAnswers, getApiQuestions } from 'reduxes/slices/api/selectors'
import {
  getSelectedWelcomeMessageId,
  getSelectedWidgetQuestionId,
} from 'reduxes/slices/selected/selectors'
import { createSelector } from 'reselect/lib/index'

export const makeCalculateQuestionGridDimensions = () =>
  createSelector(
    [getApiQuestions, getApiAnswers, (_, props) => props.welcomeMessageId],
    (questions, answers, welcomeMessageId) => {
      const minRows = 1
      const minColumns = 1
      const rootQuestion = Object.values(questions).find(
        (x) => x.welcome_message_id === welcomeMessageId && x.root,
      )

      const calcQuestion = (question, cd, rd) => {
        if (!question) return [rd, cd]
        let tempValues
        const sortedAnswers = [...question?.answers]
          .map((a) => answers[a])
          .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
        return sortedAnswers.reduce(
          (acc, answer) => {
            tempValues = calcAnswer(
              answer,
              cd,
              question.hidden_answer ? acc[0] : acc[0] + 1,
            )
            const newRd = tempValues[0]
            const newCd = tempValues[1]
            if (newRd > acc[0]) {
              acc[0] = newRd
            }
            if (newCd > acc[1]) {
              acc[1] = newCd
            }
            return acc
          },
          [rd, cd],
        )
      }
      const calcAnswer = (answer, cd, rd) => {
        if (!answer) return [rd, cd]
        let tempValues

        const hasQuestion = answer.action?.includes('question')
        if (hasQuestion) {
          const question = answer.action.split(':')[1]

          tempValues = calcQuestion(questions[question], cd + 1, rd)
          rd = tempValues[0]
          cd = tempValues[1]
        }
        const hasWelcomeMessage = answer.action?.includes('welcome_message')
        if (hasWelcomeMessage) {
          cd = cd + 1
        }
        return [rd, cd]
      }

      const fillQuestion = (question, cd, rd) => {
        if (!question) return rd
        grid[rd][cd] = { type: 'question', question }
        let tempRd = rd
        let last = false
        let prevRd
        const sortedAnswers = [...question?.answers]
          .map((a) => answers[a])
          .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
        sortedAnswers.forEach((answer, index) => {
          if (index === question.answers.length - 1) {
            last = true
          }

          tempRd = fillAnswer(
            answer,
            cd,
            question.hidden_answer ? tempRd : tempRd + 1,
            last,
            question.hidden_answer,
          )

          prevRd = tempRd
        })

        return tempRd
      }
      const fillAnswer = (answer, cd, rd, last, hidden) => {
        if (!answer) return rd
        if (!hidden) {
          grid[rd][cd] = { type: 'answer', answer, last: !!last }
        }

        const hasQuestion = answer.action?.includes('question')
        if (hasQuestion) {
          const question = answer.action.split(':')[1]
          rd = fillQuestion(questions[question], cd + 1, rd)
        }
        const hasWelcomeMessage = answer.action?.includes('welcome_message:')
        if (hasWelcomeMessage) {
          const welcomeMessageId = answer.action.split(':')[1]
          grid[rd][cd + 1] = {
            type: 'welcomeMessage',
            welcomeMessageId: parseInt(welcomeMessageId),
            answer: answer,
          }
        }
        return rd
      }
      let [maxRows, maxColumns] = calcQuestion(rootQuestion, 1, 1)

      const grid = Array.from(
        Array(maxRows + 1),
        (
          e, // plus 1 row for step heading
        ) => Array(maxColumns).fill(null),
      )
      fillQuestion(rootQuestion, 0, 1) // row start at 1 for step heading
      grid.forEach((x, xindex) => {
        return x.forEach((y, yindex) => {
          const item = y
          if (
            !item &&
            ((grid[xindex - 1]?.[yindex]?.type === 'answer' &&
              !grid[xindex - 1]?.[yindex]?.last) ||
              grid[xindex - 1]?.[yindex]?.type === 'empty')
          ) {
            grid[xindex][yindex] = { type: 'empty' }
          }
        })
      })
      return [
        Math.max(minRows, maxRows + 1),
        Math.max(minColumns, maxColumns),
        grid,
      ]
    },
  )

export const makeGetQuestion = () =>
  createSelector([getApiQuestions, (_, id) => id], (questions, id) => {
    return questions[id]
  })

export const getCurrentQuestion = createSelector(
  [getApiQuestions, getSelectedWelcomeMessageId, getSelectedWidgetQuestionId],
  (questions, welcomeMessageId, id) => {
    if (id === 'root') {
      return Object.values(questions).find(
        (q) => !!q.root && q.welcome_message_id === welcomeMessageId,
      )
    }
    return questions[id]
  },
)

export const getQuestions = createSelector(
  [getApiQuestions, getSelectedWelcomeMessageId],
  (questions, welcomeMessageId) => {
    return Object.values(questions).filter(
      (q) => q.welcome_message_id === welcomeMessageId,
    )
  },
)

export const getCurrentAnswers = createSelector(
  [
    getApiQuestions,
    getApiAnswers,
    getSelectedWelcomeMessageId,
    getSelectedWidgetQuestionId,
  ],
  (questions, answers, welcomeMessageId, id) => {
    let question
    if (id === 'root') {
      question = Object.values(questions).find(
        (q) => !!q.root && q.welcome_message_id === welcomeMessageId,
      )
    } else {
      question = questions[id]
    }
    return question?.answers
      .map((answerId) => answers[answerId])
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
  },
)

export const getOrderOfQuestions = createSelector(
  [getApiAnswers],
  (answers) => {
    return Object.values(answers).reduce((acc, answer) => {
      const q1 = answer.question_id
      const q2 = parseInt(answer.action?.replace('question:', ''))
      acc[q1] = {
        ...acc[q1],
        after: q2,
      }
      acc[q2] = {
        ...acc[q2],
        before: q1,
      }
      return acc
    }, {})
  },
)
